import { QueryExpr } from '@apis/Resources';
import { QuerySelectExpr } from '@apis/Resources/model';
import { ActionIcon, Group, Menu, NumberInput, SegmentedControl, Select, Sx, useMantineTheme } from '@mantine/core';
import {
    ArrowBigDownLines,
    CurrencyDollar,
    SortAscendingLetters,
    SortAscendingNumbers,
    SortDescendingLetters,
    SortDescendingNumbers,
} from 'tabler-icons-react';
import { ChartMargin, IChartReaggConfig, IPlotFieldDescriptor } from '@root/Components/Charts/Common';
import { useToggle } from '@root/Services/EventEmitter';
import { NamedFormats, useFmtSvc } from '@root/Services/FormatService';
import { cleanBoolExpr } from '@root/Services/QueryExpr';
import { observer } from 'mobx-react';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { SettingsDivider } from './Design';
import { IChartEditor } from './Models';
import { TooltipWhite, useReadonlyInputStyles } from '@root/Design/Primitives';
import { DatasourceSchemaContext, useContextualFilterProps, useDatasourceSchemaCtx, withResolvedSchemaCtx } from '../../Filter/DatasourceContext';
import { SettingsInputRow, SettingsLabel } from '@root/Design/Settings';
import { OptionMenuButton, OptionMenuItems } from '@root/Components/Picker/OptionMenu';
import { FilterSettingsSection } from '@root/Components/Settings/FilterSettings';
import { getUfTokens } from '@root/Components/Settings/ExprSettings';

export const EditorFilterAccordion = withResolvedSchemaCtx(function EditorFilterAccordion({
    editor,
    schemaCtx,
}: {
    editor: IChartEditor;
    schemaCtx: DatasourceSchemaContext;
}) {
    const fmtSvc = useFmtSvc();
    const { noun, nounPlural } = editor.getDatasourceDescription();
    const sectionItemTitle = `${fmtSvc.titleCase(noun)} Filters`;
    const helpText = `(Optional) Add filters to limit which ${nounPlural} are applicable for this tile.`;
    const filterProps = useContextualFilterProps();
    const handleChange = useCallback(
        (filters: QueryExpr[]) => {
            editor.setFilters(filters.map((q) => cleanBoolExpr(q) as QueryExpr).filter((q) => !!q) ?? []);
        },
        [editor]
    );

    const initialFilters = useMemo(() => {
        const filters = editor.getFilters();
        return filters?.length
            ? filters
            : editor
                  .getDatasource()
                  ?.getDefaultFilterFields?.()
                  .map((f) => ({ Operation: 'eq', Operands: [{ Field: f }] })) ?? [];
    }, [editor]);

    return editor.hideFilter ? (
        <></>
    ) : (
        <FilterSettingsSection value={initialFilters} onChange={handleChange} {...{ sectionItemTitle, helpText, schemaCtx, filterProps }} />
    );
});

function useEditorExpr<T extends string>(editor: IChartEditor, name: T, index: number, type: 'group' | 'value') {
    return useMemo(
        () => (type === 'group' ? editor.createGroupAccessor(index, name) : editor.createValueAccessor(index, name)),
        [editor, name, index, type]
    );
}
export function useRawEditorGroupExpr<T extends string>(editor: IChartEditor, name: T, index: number) {
    return useEditorExpr(editor, name, index, 'group');
}
export function useRawEditorValueExpr<T extends string>(editor: IChartEditor, name: T, index: number) {
    return useEditorExpr(editor, name, index, 'value');
}
export function useSelectExprAlias(alias: string, setter: (expr: QuerySelectExpr) => void) {
    return useCallback((expr: QuerySelectExpr) => setter({ ...expr, Alias: alias }), [setter, alias]);
}
/** Wrap a querySelectExpr setter to auto-update the alias to a user-friendly name when the expr changes */
export function useSelectExprUserDefinedAlias(
    expr: QuerySelectExpr | undefined,
    setter: (expr: QuerySelectExpr) => void
): (nextExpr: QuerySelectExpr) => void {
    const schemaCtx = useDatasourceSchemaCtx();
    const queryDescriptorSvc = schemaCtx && typeof schemaCtx !== 'string' ? schemaCtx.queryDescriptorSvc : undefined;

    return useCallback(
        (nextExpr: QuerySelectExpr) => {
            const currentDefaultAlias = !queryDescriptorSvc ? undefined : getUfTokens(expr?.Expr as QueryExpr, queryDescriptorSvc).name;
            const nextDefaultAlias = !queryDescriptorSvc ? undefined : getUfTokens(nextExpr.Expr as QueryExpr, queryDescriptorSvc).name;
            const aliasOverridden = expr?.Alias !== currentDefaultAlias;
            const shouldUpdateAlias = nextDefaultAlias && nextDefaultAlias !== currentDefaultAlias && !aliasOverridden;
            if (shouldUpdateAlias) {
                setter({ ...nextExpr, Alias: nextDefaultAlias });
            } else {
                setter(nextExpr);
            }
        },
        [setter, JSON.stringify(expr), queryDescriptorSvc]
    );
}
/** Wrap a querySelectExpr setter to auto-update the formatter when the expr changes */
export function useSelectExprFormat<T extends string | NamedFormats>(
    expr: QuerySelectExpr | undefined,
    fmt: T | undefined,
    exprSetter: (expr: QuerySelectExpr) => void,
    formatSetter: undefined | ((fmt: T) => void)
): (nextExpr: QuerySelectExpr) => void {
    const schemaCtx = useDatasourceSchemaCtx();
    const queryDescriptorSvc = schemaCtx && typeof schemaCtx !== 'string' ? schemaCtx.queryDescriptorSvc : undefined;

    return useCallback(
        (nextExpr: QuerySelectExpr) => {
            if (queryDescriptorSvc && formatSetter) {
                const currentType = expr?.Expr ? queryDescriptorSvc.getExprType(expr.Expr as QueryExpr) : undefined;
                const nextType = nextExpr.Expr ? queryDescriptorSvc.getExprType(nextExpr.Expr as QueryExpr) : undefined;
                const currentExprDefaultFormat = expr?.Expr ? (queryDescriptorSvc.getFormatter(expr.Expr as QueryExpr)?.id as T) : undefined;
                const nextFormat = (queryDescriptorSvc.getFormatter(nextExpr.Expr as QueryExpr)?.id as T) ?? (nextType as T);
                const fmtOverridden = fmt !== currentExprDefaultFormat;
                const shouldUpdateFmt = (nextType && nextType !== currentType) || (nextFormat !== fmt && !fmtOverridden);
                if (shouldUpdateFmt && formatSetter) {
                    formatSetter(nextFormat ?? 'string');
                }
            }
            exprSetter(nextExpr);
        },
        [exprSetter, formatSetter, fmt, JSON.stringify(expr), queryDescriptorSvc]
    );
}

type EditorSelectExprOptions = {
    format?: string;
    formatSetter?: ((fmt: NamedFormats | undefined) => void) | ((fmt: string | undefined) => void);
    aliasType?: 'index-based' | 'user-friendly';
};
function useEditorSelectExpr(
    editor: IChartEditor,
    index: number,
    type: 'group' | 'value',
    options?: EditorSelectExprOptions
): [QuerySelectExpr | undefined, (nextExpr: QuerySelectExpr) => void, () => void] {
    const { format, formatSetter, aliasType } = options ?? {};
    const { expr, setExpr: baseSetExpr, removeExpr } = useEditorExpr(editor, 'expr', index, type);

    const setIndexBasedAliasedExpr = useSelectExprAlias(type + index, baseSetExpr);
    const setUserFriendlyAliasedExpr = useSelectExprUserDefinedAlias(expr, baseSetExpr);
    const setAliasedExpr = aliasType !== 'user-friendly' ? setIndexBasedAliasedExpr : setUserFriendlyAliasedExpr;

    const setFormatterByExpr = useSelectExprFormat(
        expr,
        format ?? 'string',
        setAliasedExpr,
        formatSetter as (fmt: string | NamedFormats | undefined) => void
    );
    const setFormatAndExpr = !formatSetter ? setAliasedExpr : setFormatterByExpr;

    return [expr, setFormatAndExpr, removeExpr];
}
export function useEditorGroupSelectExpr(editor: IChartEditor, index: number, options?: EditorSelectExprOptions) {
    return useEditorSelectExpr(editor, index, 'group', options);
}
export function useEditorValueSelectExpr(editor: IChartEditor, index: number, options?: EditorSelectExprOptions) {
    return useEditorSelectExpr(editor, index, 'value', options);
}

export const castSetter = function <TFrom, TTo>(setter: (value: TFrom) => void) {
    return setter as unknown as (value: TTo) => void;
};

type TValueAccessor<TSettings, TProp extends string & keyof TSettings> = {
    getValue: () => TSettings[TProp];
    setValue: (value: TSettings[TProp]) => void;
};
type AllowedKeys<T, V> = { [K in keyof T]: T[K] extends V ? K : never }[string & keyof T];

function createValueAccessor<TSettings, TProp extends AllowedKeys<TSettings, TDefaultValue>, TDefaultValue = TSettings[TProp]>(
    settings: TSettings,
    property: TProp,
    defaultValue?: TDefaultValue
): TValueAccessor<TSettings, TProp> {
    return {
        getValue() {
            if (!(property in settings) && defaultValue !== undefined) {
                settings[property] = defaultValue as unknown as TSettings[TProp];
            }
            return settings[property];
        },
        setValue(value: TSettings[TProp]) {
            settings[property] = value;
        },
    };
}

export function useValueAccessor<TSettings, TProp extends AllowedKeys<TSettings, TDefaultValue>, TDefaultValue = TSettings[TProp]>(
    settings: TSettings,
    property: TProp,
    defaultValue?: TDefaultValue
): TValueAccessor<TSettings, TProp> {
    return useMemo(() => createValueAccessor<TSettings, TProp, TDefaultValue>(settings, property, defaultValue), [settings, property]);
}

type TGetterSetter<TSettings, TProp extends string & keyof TSettings> = { [K in TProp]: TSettings[K] } & {
    [K in TProp as `set${Capitalize<K>}`]: (value: TSettings[K]) => void;
};
export function useGetterSetter<TSettings, TProp extends AllowedKeys<TSettings, any>>(
    settings: TSettings,
    ...properties: TProp[]
): TGetterSetter<TSettings, TProp> {
    return useMemo(() => {
        const accessors = properties.map((p) => ({
            getterName: p,
            setterName: `set${p[0].toUpperCase()}${p.slice(1)}`,
            accessor: createValueAccessor<TSettings, TProp, any>(settings, p),
        }));
        return accessors.reduce((result, { getterName, setterName, accessor: { getValue, setValue } }) => {
            return Object.defineProperties(result, {
                [getterName]: { get: getValue },
                [setterName]: { value: setValue },
            });
        }, {} as TGetterSetter<TSettings, TProp>);
    }, [settings, JSON.stringify(properties)]);
}

export const ChartReaggOptions = observer(function ChartReaggOptions<
    TSettings,
    TMpProp extends AllowedKeys<TSettings, IChartReaggConfig | undefined>
>(props: { settings: TSettings; reaggProp: TMpProp; limitLbl: string; autoLimit?: boolean; max?: number }) {
    const { settings, reaggProp: multiplotProp, limitLbl, autoLimit, max } = props;
    const { getValue } = useValueAccessor(settings, multiplotProp, {});
    const reaggOptions = getValue() as IChartReaggConfig;
    const { limit, setLimit, sortBy, sortDir, setSortBy, setSortDir } = useGetterSetter(reaggOptions, 'limit', 'otherLabel', 'sortBy', 'sortDir');

    const setSort = useCallback(
        ({ sortBy, sortDir }: IChartReaggConfig) => {
            setSortBy(sortBy);
            setSortDir(sortDir);
        },
        [setSortBy, setSortDir]
    );
    const sortOptions = useMemo(
        () =>
            [
                { label: 'Values Low to High', sortBy: 'value', sortDir: 'asc', icon: SortAscendingNumbers },
                { label: 'Values High to Low', sortBy: 'value', sortDir: 'desc', icon: SortDescendingNumbers },
                { label: 'Labels A to Z', sortBy: 'label', sortDir: 'asc', icon: SortAscendingLetters },
                { label: 'Labels Z to A', sortBy: 'label', sortDir: 'desc', icon: SortDescendingLetters },
            ].map(
                ({ icon: Icon, label, ...o }) =>
                    ({
                        ...o,
                        onClick: () => setSort(o as IChartReaggConfig),
                        icon: <Icon size={16} />,
                        label: `Sort by ${label}`,
                        selected: sortBy === o.sortBy && sortDir === o.sortDir,
                    } as OptionMenuButton)
            ),
        [sortBy, sortDir, setSort]
    );
    const { icon, label } = sortOptions.find((o) => o.selected) ?? sortOptions[1];
    const sortDescription = (label as string).replace('Sort', 'sorted').toLowerCase();
    const tooltip = autoLimit ? `Automatically show top ${limitLbl}s, ${sortDescription}` : `Show top ${limit} ${limitLbl}s, ${sortDescription}`;

    const { classes } = useReadonlyInputStyles();
    const numberSx: Sx = { width: 75, ['input']: { textAlign: 'center', paddingLeft: 0 }, ['input:disabled']: { cursor: 'default' } };
    const [sortMenuOpened, { close: closeSortMenu, toggle: toggleSortMenu }] = useToggle(false);
    const valueMax = max ?? 20;
    return (
        <>
            <SettingsInputRow>
                <SettingsLabel icon={<ArrowBigDownLines />}>{limitLbl} limit</SettingsLabel>
                <TooltipWhite offset={0} openDelay={500} disabled={sortMenuOpened} withinPortal label={tooltip}>
                    <Group spacing={6}>
                        {autoLimit ? (
                            <NumberInput className={classes.readonly} disabled sx={numberSx} pl={0} size="xs" placeholder="Auto" />
                        ) : (
                            <NumberInput sx={numberSx} pl={0} max={valueMax} min={1} size="xs" value={limit} onChange={setLimit} placeholder="None" />
                        )}
                        <Menu opened={sortMenuOpened} onClose={closeSortMenu} withinPortal position="bottom-end">
                            <Menu.Target>
                                <ActionIcon onClick={toggleSortMenu} sx={{ borderColor: 'var(--input-border-color)' }} variant="outline">
                                    {icon}
                                </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <OptionMenuItems options={sortOptions} close={closeSortMenu} />
                            </Menu.Dropdown>
                        </Menu>
                    </Group>
                </TooltipWhite>
            </SettingsInputRow>
        </>
    );
});

export function useDescriptorUpdates(setDescriptors: (descriptors: IPlotFieldDescriptor[]) => void, ...exprs: Array<undefined | QuerySelectExpr>) {
    const schemaCtx = useDatasourceSchemaCtx();
    const queryDescriptorSvc = schemaCtx && typeof schemaCtx !== 'string' ? schemaCtx.queryDescriptorSvc : undefined;
    useEffect(() => {
        if (queryDescriptorSvc) {
            const descriptors: IPlotFieldDescriptor[] = [];
            for (const expr of exprs) {
                if (expr?.Alias) {
                    const name = queryDescriptorSvc.getName(expr.Expr as QueryExpr);
                    descriptors.push({ field: expr.Alias, details: [{ label: name, value: '' }] });
                }
            }
            setDescriptors(descriptors);
        }
    }, [JSON.stringify(exprs), setDescriptors, queryDescriptorSvc]);
}

export const ChartMargins = observer(function ChartMargins({ settings: { margin } }: { settings: { margin?: Partial<ChartMargin> } }) {
    return (
        <>
            <SettingsDivider />
            <SettingsLabel icon="ti ti-box-margin">Margins</SettingsLabel>
            <Group position="center">
                <NumberInput
                    label="Top"
                    size="xs"
                    sx={{ width: '6rem' }}
                    value={margin!.top ?? 0}
                    onChange={(value) => (margin!.top = value || 0)}
                    step={5}
                />
            </Group>
            <Group position="apart">
                <NumberInput
                    label="Left"
                    size="xs"
                    sx={{ width: '6rem' }}
                    value={margin!.left ?? 0}
                    onChange={(value) => (margin!.left = value || 0)}
                    step={5}
                />
                <NumberInput
                    label="Right"
                    size="xs"
                    sx={{ width: '6rem' }}
                    value={margin!.right ?? 0}
                    onChange={(value) => (margin!.right = value || 0)}
                    step={5}
                />
            </Group>
            <Group position="center">
                <NumberInput
                    label="Bottom"
                    size="xs"
                    sx={{ width: '6rem' }}
                    value={margin!.bottom ?? 0}
                    onChange={(value) => (margin!.bottom = value || 0)}
                    step={5}
                />
            </Group>
        </>
    );
});

export const NumberSettings = observer(function NumberSettings(
    props: NumberSettingsInputProps & {
        label: string;
        icon?: ReactNode;
    }
) {
    const { label, icon, ...inputProps } = props;
    return (
        <SettingsInputRow>
            <SettingsLabel icon={icon}>{label}</SettingsLabel>
            <NumberSettingsInput {...inputProps} />
        </SettingsInputRow>
    );
});
interface NumberSettingsInputProps {
    value: number | undefined | null;
    onChange: (value: null | number | undefined) => void;
    decimals?: number;
    min?: number;
    max?: number;
    width?: number;
    center?: boolean;
}
export const NumberSettingsInput = observer(function NumberSettingsInput(props: NumberSettingsInputProps) {
    const { value, decimals, onChange, min, max, center, width = 60 } = props;
    const numberSx: Sx = { width, ['input']: !center ? {} : { textAlign: 'center', paddingLeft: 0 }, ['input:disabled']: { cursor: 'default' } };
    return (
        <NumberInput
            size="xs"
            sx={numberSx}
            min={min}
            max={max}
            value={value === null ? undefined : value}
            precision={decimals ?? 0}
            onChange={onChange}
        ></NumberInput>
    );
});

export const DropdownSettings = observer(function DropdownSettings({
    value,
    onChange,
    options,
    label,
    width = 140,
    icon,
    center,
    clearable,
}: {
    label: string;
    value: string | undefined | null;
    onChange: (value: null | string | undefined) => void;
    options: { value: string; label: string }[];
    width?: number;
    icon?: ReactNode;
    center?: boolean;
    clearable?: boolean;
}) {
    const inputSx: Sx = {
        width: width,
        input: { textAlign: center ? 'center' : undefined },
        ['.mantine-Select-dropdown *']: { textAlign: center ? 'center' : undefined },
    };

    return (
        <Group position="apart">
            <SettingsLabel icon={icon}>{label}</SettingsLabel>
            <Select size="xs" sx={inputSx} clearable={clearable} onChange={onChange} data={options} value={value}></Select>
        </Group>
    );
});

export const SegmentedControlSettings = observer(function SegmentedControlSettings({
    value,
    onChange,
    options,
}: {
    value: string;
    options: { value: string; label: string }[];
    onChange: (value: null | string | undefined) => void;
}) {
    const { colors } = useMantineTheme();
    const sx = {
        border: 'solid 1px var(--input-border-color)',
        padding: '1px 4px',
        background: '#fff',
        height: 'var(--input-height)',
        lineHeight: '20px',
        ['.mantine-SegmentedControl-active']: {
            marginTop: 4,
            height: 'calc(var(--input-height) - 6px)',
            border: `solid 1px ${colors.gray[4]}`,
            background: colors.primary[1],
        },
    };
    return <SegmentedControl size="xs" sx={sx} value={value} data={options} onChange={onChange} />;
});

export function FormatSettings({
    value,
    onChange,
    type,
}: {
    value: string | undefined | null;
    onChange: (value: null | string | undefined) => void;
    type: string;
}) {
    const fmtSvc = useFmtSvc();
    const options = useMemo(() => {
        const result = (type === 'string' ? [] : fmtSvc.getNamedFormatters(type)).map((o) => ({ value: o.id as string, label: o.ufName! }));
        if (result.length) {
            result.unshift({ value: '', label: 'None' });
        }
        return result;
    }, [type]);
    const handleChange = useCallback(
        (value: string | null | undefined) => {
            onChange(value === '' ? undefined : value);
        },
        [onChange]
    );
    return !options.length ? (
        <></>
    ) : (
        <DropdownSettings icon={<CurrencyDollar />} width={165} options={options} onChange={handleChange} label="Format" value={value ?? ''} />
    );
}
